import { useTranslation } from 'react-i18next';
import Layout from '@/components/feature/Layout';

export default function Contact() {
  const { t } = useTranslation();

  const contactInfo = [
    {
      icon: 'ri-phone-line',
      label: t('contactPage.phone'),
      value: '+49 (0) 2351 / 5 21 33',
      href: 'tel:+49235152133',
    },
    {
      icon: 'ri-printer-line',
      label: t('contactPage.fax'),
      value: t('contactPage.faxNumber'),
      href: 'tel:+49235152670',
    },
    {
      icon: 'ri-mail-line',
      label: t('contactPage.email'),
      value: 'sales@bock-mf.de',
      href: 'mailto:sales@bock-mf.de',
    },
  ];

  return (
    <Layout>
      {/* Page Header */}
      <div className="bg-foreground-900 pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <span className="text-accent-400 text-sm font-medium uppercase tracking-wider mb-4 block">
            {t('nav.contact')}
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            {t('contactPage.title')}
          </h1>
          <p className="text-white/60 text-base md:text-lg max-w-2xl">
            {t('contactSection.title')}
          </p>
        </div>
      </div>

      {/* Contact Content */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Info */}
            <div>
              <div className="bg-background-50 rounded-xl border border-background-200 p-8 mb-8">
                <h3 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
                  {t('contactPage.address')}
                </h3>
                <div className="space-y-2 text-foreground-700 mb-8">
                  <p className="font-medium">{t('contactPage.addressLine1')}</p>
                  <p>{t('contactPage.addressLine2')}</p>
                  <p>{t('contactPage.addressLine3')}</p>
                </div>

                <div className="space-y-4">
                  {contactInfo.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      className="flex items-center gap-4 p-4 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center group-hover:bg-primary-500 transition-colors">
                        <i className={`${item.icon} text-xl text-primary-500 group-hover:text-white transition-colors`}></i>
                      </div>
                      <div>
                        <p className="text-sm text-foreground-500">{item.label}</p>
                        <p className="font-medium text-foreground-900 group-hover:text-primary-700 transition-colors">
                          {item.value}
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              <div className="bg-accent-50 rounded-xl border border-accent-200 p-6">
                <div className="flex items-center gap-3">
                  <i className="ri-time-line text-accent-500 text-xl"></i>
                  <div>
                    <p className="font-medium text-accent-900">Business Hours</p>
                    <p className="text-sm text-accent-700">Mon - Fri: 8:00 AM - 5:00 PM CET</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-xl overflow-hidden shadow-lg border border-background-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2523.8!2d7.6333!3d51.2167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b8c0b0!2sLuedenscheid!5e0!3m2!1sen!2sde!4v1"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '500px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Maschinenfabrik Bock Location"
                className="grayscale-[20%]"
              ></iframe>
            </div>
          </div>

          {/* Geschäftsführer & Verwaltung */}
          <div className="mt-16 md:mt-24">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8">
              {t('contactPage.team')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              {/* Karsten Bock */}
              <div className="bg-background-50 rounded-2xl border border-background-200 p-8 flex items-start gap-6">
                <div className="flex-shrink-0">
                  <img
                    src="https://www.bock-mf.de/typo3temp/pics/78f22aa90f.png"
                    alt="Dipl.-Ing. Karsten Bock"
                    className="w-[180px] h-[255px] object-contain rounded-xl bg-white shadow-sm"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
                <div className="space-y-3">
                  <h3 className="font-heading text-xl font-bold text-foreground-900">
                    Dipl.-Ing. Karsten Bock
                  </h3>
                  <p className="text-primary-600 font-medium text-sm">
                    {t('contactPage.managingDirector')}
                  </p>
                  <div className="space-y-2 text-foreground-600 text-sm">
                    <p>
                      <span className="text-foreground-500">Telefon:</span>{' '}
                      <a href="tel:+49235152133" className="text-foreground-900 hover:text-primary-600 transition-colors">
                        +49 (0) 2351 / 5 21 33
                      </a>
                    </p>
                    <p>
                      <span className="text-foreground-500">E-Mail:</span>{' '}
                      <a href="mailto:sales@bock-mf.de" className="text-foreground-900 hover:text-primary-600 transition-colors">
                        sales@bock-mf.de
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Jutta Kordt-Bock */}
              <div className="bg-background-50 rounded-2xl border border-background-200 p-8 flex items-start gap-6">
                <div className="flex-shrink-0">
                  <img
                    src="https://www.bock-mf.de/typo3temp/pics/1ba071bf53.png"
                    alt="Jutta Kordt-Bock"
                    className="w-[180px] h-[255px] object-contain rounded-xl bg-white shadow-sm"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
                <div className="space-y-3">
                  <h3 className="font-heading text-xl font-bold text-foreground-900">
                    Jutta Kordt-Bock
                  </h3>
                  <p className="text-primary-600 font-medium text-sm">
                    {t('contactPage.administration')}
                  </p>
                  <div className="space-y-2 text-foreground-600 text-sm">
                    <p>
                      <span className="text-foreground-500">Telefon:</span>{' '}
                      <a href="tel:+49235152133" className="text-foreground-900 hover:text-primary-600 transition-colors">
                        +49 (0) 2351 / 5 21 33
                      </a>
                    </p>
                    <p>
                      <span className="text-foreground-500">E-Mail:</span>{' '}
                      <a href="mailto:sales@bock-mf.de" className="text-foreground-900 hover:text-primary-600 transition-colors">
                        sales@bock-mf.de
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}